
import { useTranslation } from "react-i18next";
import NewPage from "../../../layouts/page/NewPage";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useState } from "react";
import { convertBooleanToStatus } from "../../../../utils/ConverterUtils";
import MenuConfig from "../../../../constants/MenuConfig";
import Form from "./Form";
import { getId, sendToApi } from "../../../../utils/ApiUtils";
import { useNavigate } from "react-router-dom";
import Api from "../../../../constants/Api";
import { ApiError } from "../../../components/Common/ApiError";
import { useBranch } from "../../../../context/BranchContext";
import axiosInstance from "../../../../services/AxiosInstance";
import { warnAlertYesOrNo } from "../../../components/alert/defaultAlert";

const BranchNew = () => {

    const { t } = useTranslation();
    const [status, setStatus] = useState(true);
    const [entityData, setEntityData] = useState(null);
    const [workingHours, setWorkingHours] = useState([]);
    const { setBranches } = useBranch();
    const navigate = useNavigate();

    // Define o schema de validação do form.
    const formSchema = Yup.object().shape({
        name: Yup.string()
            .min(3, t("validation.min.generic", { field: t('name'), qty: 3 }))
            .required(t('validation.required.name'))
    });

    // Atualiza a lista de filiais do contexto de filiais.
    const handleAddBranch = (newBranch) => {
        // Atualiza a lista de filiais
        setBranches((prevBranches) => [...prevBranches, newBranch]);
    };

    // Define o formik.
    const formik = useFormik({
        initialValues: {
            name: '',
            document: '',
            street: '',
            number: '',
            complement: '',
            neighborhood: '',
            city: '',
            state: '',
            zipCode: ''
        },
        validationSchema: formSchema,
        onSubmit: values => {
            let payload = {
                id: getId(entityData),
                name: values.name,
                document: values.document,
                address: {
                    street: values.street,
                    number: values.number,
                    complement: values.complement,
                    neighborhood: values.neighborhood,
                    city: values.city,
                    state: values.state,
                    zipCode: values.zipCode
                },
                status: convertBooleanToStatus(status),
            }

            const isInactivating = entityData?.id && entityData.status === 'ACTIVE' && !status;
            if (!isInactivating) {
                save(payload);
                return;
            }

            // Inativar a filial tira da agenda quem só atende nela: o colaborador continua ativo,
            // mas sem filial ativa some da agenda e da grade com filial selecionada. O aviso vem
            // antes de gravar para o gestor transferir essas pessoas primeiro, se quiser.
            axiosInstance.get(`${Api.main.employee}/only-in-branch/${entityData.id}`)
                .then((response) => {
                    const employees = Array.isArray(response.data) ? response.data : [];
                    if (employees.length === 0) {
                        save(payload);
                        return;
                    }

                    const names = employees.map((employee) => employee.fullName).join(', ');
                    warnAlertYesOrNo(
                        `${names} ${employees.length === 1 ? 'só tem' : 'só têm'} esta filial ativa e ${employees.length === 1 ? 'vai ficar' : 'vão ficar'} sem filial ativa, fora da agenda. Deseja inativar a filial mesmo assim?`,
                        () => save(payload),
                        () => formik.setSubmitting(false)
                    );
                })
                // Sem a consulta não dá para avisar, mas a inativação continua sendo uma decisão
                // válida do gestor: segue o fluxo normal em vez de travar o salvamento.
                .catch(() => save(payload));
        }
    });

    // Grava a filial e, em seguida, os horários de funcionamento.
    const save = (payload) => {
        // Aciona a api
        sendToApi(Api.main.branch, payload, async (resp) => {
            // Os horários dependem do ID da filial, então são persistidos
            // imediatamente após o cadastro principal no mesmo fluxo.
            const savedBranchId = entityData?.id || resp.data?.id;
            if (workingHours.length > 0 && savedBranchId) {
                await axiosInstance.put(`/main/branch-working-hours/branch/${savedBranchId}`, workingHours.map(day => ({
                    id: day.id || null,
                    branchId: savedBranchId,
                    dayOfWeek: day.dayOfWeek,
                    isOpen: day.isOpen,
                    openTime: day.isOpen ? day.openTime : null,
                    closeTime: day.isOpen ? day.closeTime : null,
                    breakStartTime: day.isOpen ? day.breakStartTime : null,
                    breakEndTime: day.isOpen ? day.breakEndTime : null
                })));
            }
            if (entityData == null) {
                onReset();

                // Atualiza a lista de filiais do contexto.
                handleAddBranch(resp.data);
            } else {
                navigate(MenuConfig.main.branch.route);
            }
        }, (error) => {
            ApiError(error);
            formik.setSubmitting(false);
        });
    };

    // Valida o form e em seguida submete os dados. Aciona o onSubmit do formik.
    const onSubmit = (e) => {
        e.preventDefault();
        formik.handleSubmit();
    }

    const onReset = () => {
        formik.resetForm({
            values: {
                name: '',
                document: '',
                street: '',
                number: '',
                complement: '',
                neighborhood: '',
                city: '',
                state: '',
                zipCode: ''
            }
        });
        setStatus(true);
    }

    return (
        <>
            <NewPage
                motherMenu={t('main')}
                childMenu={t('branches')}
                activeMenu={t('new')}
                child={
                    <Form
                        formik={formik}
                        status={status}
                        setStatus={setStatus}
                        entityData={entityData}
                        onWorkingHoursChange={setWorkingHours}
                    />
                }
                setEntityData={setEntityData}
                path={Api.main.branch}
                isSubmitting={formik.isSubmitting}
                handleSubmit={onSubmit}
                cancelRoute={MenuConfig.main.branch.route}
            />
        </>
    );
}

export default BranchNew;
